import { QueryCache, QueryClient } from "@tanstack/react-query";

import { handleError, logErrorHandlingFailure } from "../app/errors";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { handlesOwnErrors?: boolean };
  }
}

/**
 * Global TanStack Query client.
 *
 * The defaults deliberately match the app's behavior from before TanStack
 * Query: no automatic retries, and no automatic refetching on window focus
 * or network reconnect (a failed background refetch reaches the global error
 * handler, so a flaky connection would pop error dialogs over a view that is
 * still showing its data). With the default `staleTime: 0`, remounting a
 * component shows cached data instantly and refreshes it in the background.
 *
 * A failed query that a mounted component is watching reaches the global error
 * handler, as a failed load did before. A query that handles its own failure
 * opts out with `meta: { handlesOwnErrors: true }`.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.handlesOwnErrors || query.getObserversCount() === 0) {
        return;
      }
      handleError(error).catch(logErrorHandlingFailure);
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

/**
 * Mark every cached read stale after a write, so a read kept fresh for a while
 * (the AI summary's facts) is read again the next time it mounts. Nothing
 * refetches here: a view on screen reloads itself after its own write.
 */
export function markReadsStale() {
  void queryClient.invalidateQueries({ refetchType: "none" });
}
